<template>
  <NavigationMenuItem :value="route.path">
    <template v-if="children.length > 0">
      <details :open="active">
        <NavigationMenuTrigger
          class="flex w-full items-center gap-2 after:md:content-none"
          as="summary"
          @click.prevent
          @keydown.prevent
        >
          <FontAwesomeIcon v-if="route.meta?.icon" :icon="route.meta?.icon" />
          <span class="mr-auto text-nowrap">
            {{ route.name ? $t(`routes.${route.name.toString()}.title`) : '' }}
          </span>
          <FontAwesomeIcon v-if="children.length > 0 && isDesktop" :icon="faChevronRight" />
        </NavigationMenuTrigger>
        <NavigationMenuContent as-child>
          <NavigationMenuSub
            v-if="hasSubmenu"
            :default-value="children[0]?.path"
            class="gap-1 overflow-y-auto md:flex"
            orientation="vertical"
          >
            <template v-slot="{ modelValue }">
              <NavigationMenuList as="ul" class="md:menu md:menu-lg">
                <RouterNavItem
                  v-for="child in children"
                  :key="child.path"
                  :route="child"
                  :prefix="path"
                  :user-roles="userRoles"
                  :active="child.path === modelValue"
                  :level="level + 1"
                />
              </NavigationMenuList>
              <div v-if="isDesktop" class="flex justify-self-stretch border-l">
                <NavigationMenuViewport align="start" />
              </div>
            </template>
          </NavigationMenuSub>
          <ul v-else class="md:menu md:menu-lg overflow-y-auto">
            <RouterNavItem
              v-for="child in children"
              :key="child.path"
              :route="child"
              :prefix="path"
              :user-roles="userRoles"
              :level="level + 1"
            />
          </ul>
        </NavigationMenuContent>
      </details>
    </template>
    <NavigationMenuLink v-else :as="RouterLink" :to="path" :class="linkClass">
      <FontAwesomeIcon v-if="route.meta?.icon" :icon="route.meta?.icon" />
      <span class="text-nowrap">
        {{ route.name ? $t(`routes.${route.name.toString()}.title`) : '' }}
      </span>
    </NavigationMenuLink>
  </NavigationMenuItem>
</template>

<script setup lang="ts">
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { UserRole } from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuSub,
  NavigationMenuTrigger,
  NavigationMenuViewport,
} from 'reka-ui';
import { computed } from 'vue';
import { RouterLink, type RouteRecordRaw } from 'vue-router';
import { useDeviceType } from '../composables/style/useDeviceType';
import { isTextMenuRoute, sortMenuRoutes } from '../router/menu';

const props = withDefaults(
  defineProps<{
    prefix: string;
    route: RouteRecordRaw;
    userRoles: UserRole[];
    active?: boolean;
    level?: number;
  }>(),
  {
    level: 0,
  },
);

const { isDesktop } = useDeviceType();

const children = computed(() => sortMenuRoutes(filterVisibleRoutes(props.route.children ?? [])));

const linkClass = computed(() =>
  isTextMenuRoute(props.route)
    ? 'link link-hover text-base-content/70 flex items-center justify-end gap-2 bg-transparent text-sm font-normal hover:bg-transparent focus:bg-transparent'
    : undefined,
);

const hasSubmenu = computed(() =>
  children.value.some((child) => child.children && filterVisibleRoutes(child.children).length > 0),
);

const path = computed(() => {
  const {
    prefix,
    route: { path },
  } = props;

  return prefix ? `${prefix.replace(/\/$/, '')}/${path}` : path;
});

const filterVisibleRoutes = (routes: RouteRecordRaw[]) => {
  return routes.filter(
    (route) =>
      route.meta &&
      !route.meta.hide &&
      (!route.meta.requiresRole ||
        route.meta.requiresRole.some((role) => props.userRoles.includes(role))) &&
      !route.meta.excludeRoles?.some((role) => props.userRoles.includes(role)),
  );
};
</script>
